import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore, useAwardStore } from "@/stores";
import type { Role } from "best-of-awards-api";
import adminRoutes from "./admin";
import candidateRoutes from "./candidate";
import commonRoutes from "./common";
import getAwardItems from "@/util/get-award-items";
import { scrollTop } from "@/util/window";
import semver from "semver";

declare module "vue-router" {
  interface RouteMeta {
    authorizedRoles?: Role[];
    friendlyName?: string;
    hideNav?: boolean;
    isAdmin?: boolean;
    requiresAuth: boolean;
  }
}

export const router = createRouter({
  history: createWebHistory("/dashboard"),
  routes: [...commonRoutes, ...candidateRoutes, ...adminRoutes],
  scrollBehavior: () => {
    scrollTop();
  },
});

export const defaultRoutes: Partial<Record<Role, { name: string }>> = {
  ["Admin"]: {
    name: "home",
  },
  ["Candidate"]: {
    name: "home",
  },
};

// navigation guards are processed in creation order
router.beforeEach(async (to) => {
  const authStore = useAuthStore();
  if (
    authStore.authenticated &&
    authStore.isCandidate &&
    !authStore.user.candidateVerified &&
    to.name !== "home"
  ) {
    return { name: "home" };
  }
});

router.beforeEach(async (to) => {
  const authStore = useAuthStore();
  const awardStore = useAwardStore();

  document.title = awardStore.activeAward.settings?.name || "Best of Awards";
  if (to.meta.friendlyName) {
    document.title = `${document.title} - ${to.meta.friendlyName}`;
  }

  // verify authenticated and authorized
  if (to.meta?.requiresAuth) {
    let authenticated = authStore.authenticated;

    if (!authenticated) {
      ({ authenticated } = await authStore.login({
        username: "",
        password: "",
      }));
      await getAwardItems(authStore.user.role as Role);
    }

    if (authenticated) {
      const { authorizedRoles = [] } = to.meta;
      if (!authorizedRoles.length) {
        return;
      }
      for (const role of authStore.user.roles) {
        if (authorizedRoles.includes(role as Role)) {
          return;
        }
      }
      return defaultRoutes[authStore.user.role];
    } else {
      return { name: "login" };
    }
  }
});

router.afterEach(() => {
  const authStore = useAuthStore();
  try {
    const latestClientVersion = semver.valid(authStore.latestClientVersion);
    const currentClientVersion = semver.valid(
      import.meta.env.VITE_BOA_CLIENT_VERSION
    );
    console.log({ latestClientVersion });
    console.log({ currentClientVersion });
    if (
      latestClientVersion &&
      currentClientVersion &&
      semver.gt(latestClientVersion, currentClientVersion)
    ) {
      console.log("out of date");
      if (!authStore.reloading) {
        authStore.reloading = true;
        window.location.reload();
      }
    }
  } catch (error) {
    console.error(error);
  }
});

export default router;
